// Angular-Module
import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
// ReactiveX for JavaScript
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
// Service des übergeordneten Feature-Moduls
import {InstitutionsService} from '../institutions.service';
import {InstitutionsPublicNoteService} from './institutions-public-note.service';
import {StorageService} from '@global/services/storage.service';

@Component({
    selector: 'phscw-institutions-public-note',
    standalone: false,
    templateUrl: './institutions-public-note.component.html',
    styleUrl: './institutions-public-note.component.scss',
})
export class InstitutionsPublicNoteComponent implements OnInit, OnDestroy {

    // Wird bei ngOnDestroy ausgelöst um Observables-Subscription zu stoppen
    private componentDestroyed$ = new Subject<void>();

    // Referenz auf Form
    @ViewChild('institutionsPublicNoteForm', {static: false}) institutionsPublicNoteForm: NgForm;

    // ID der aktuell ausgewählten Notiz
    noteId: number = null;
    publicNote: string = '';
    readOnlyNote: string;
    markdownRender = false;
    contactsAndNotesMarkdownEnabled = false;

    // EditMode aktiv?
    @Input() editMode = false;

    // Flag definiert ob gerade geladen wird
    loading = false;
    // Flag definiert ob gerade gespeichert wird
    saving = false;
    institutionId;

    /**
     * Konstruktor (inkl. dependency injection)
     * @param gridService
     * @param institutionsService
     * @param InstitutionsPublicNoteService
     * @param peopleNotesService
     * @param toolbarService
     * @param overlayService
     * @param dialog
     * @param translateService
     */
    constructor(
        private institutionsService: InstitutionsService,
        private institutionsPublicNoteService: InstitutionsPublicNoteService,
        private storageService: StorageService,
    ) {}

    /**
     * Initialisieren
     */
    ngOnInit() {
        // Markdown Config laden
        this.storageService.getItem('config|contactsAndNotesMarkdownEnabled').then((value) => {
            if (value !== null && value !== undefined) {
                this.contactsAndNotesMarkdownEnabled = value;
            }
        });
        // Events subscriben
        this.initializeEventSubscriptions();
    }

    /**
     * Aufräumen
     */
    ngOnDestroy() {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    /**
     * Events subscriben
     */
    initializeEventSubscriptions(): void {
        // In der E-Liste wird eine Einrichtung angeklickt
        this.institutionsService.selectionChanged
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((result: number) => {
                this.onSelectionChanged(result);
            });
    }

    /**
     * Auf geänderte Auswahl (Einrichtung) reagieren
     * @param id
     */
    onSelectionChanged(id: number): void {
        this.institutionId = id;
        // Falls sich evtl. eine Notiz im editMode befindet, wird dies abgebrochen
        this.clickCancel();
        // Bereits vorhandene Daten (einer anderen Einrichtung) entfernen
        this.resetData();
        // Falls eine existente Einrichtung ausgewählt wurde
        if (this.institutionId > 0) {
            // ... Daten laden
            this.loadDetails();
        }
    }

    /**
     * Einelne Notiz laden
     * @param noteType
     */
    loadDetails(): void {
        this.noteId = null;
        this.readOnlyNote = null;

        // Wenn keine gültige ID vorliegt, abbrechen
        if (!this.institutionId || this.institutionId <= 0) {
            return;
        }
        this.loading = true;

        let serviceRequest$ = null;
        serviceRequest$ = this.institutionsPublicNoteService.loadPublicNoteDetails(this.institutionId);

        serviceRequest$.subscribe((response) => {
            // Prüft und setzt die readonlyNote
            if (response?.public_note?.readonly_note) {
                this.readOnlyNote = response.public_note.readonly_note;
            } else {
                this.readOnlyNote = null;
            }
            // Prüft und setzt die publicNote
            if (response?.public_note?.note) {
                this.publicNote = response.public_note.note;
                this.noteId = response.public_note.id;
                this.markdownRender = response.public_note.markdown_render ?? false;
            } else {
                this.noteId = null;
                this.markdownRender = false;
            }
        });
        // Flag "loading" deaktivieren
        this.loading = false;
    }

    /**
     * Klick auf "Speichern" (Form-Submit)
     */
    clickSubmit(): void {
        // Nur gültige Formulare werden submitted
        if (this.institutionsPublicNoteForm.form.valid) {
            const formValues = this.institutionsPublicNoteForm.form.value;

            const payload = {
                id: this.institutionId,
                public_note: {
                    ...(this.noteId && {id: this.noteId}),
                    institution_id: this.institutionId,
                    region_id: 0,
                    note: formValues.note,
                },
            };
            // Flag "Speichern" aktivieren
            this.saving = true;
            let serviceRequest$ = null;

            serviceRequest$ = this.institutionsPublicNoteService.saveData(
                this.institutionId,
                payload,
            );

            serviceRequest$.subscribe(() => {
                // EditMode verlassen
                this.editMode = false;
                // Flag "saving" deaktivieren
                this.saving = false;
                // Daten zurücksetzen
                this.resetData();
                // Neu laden
                this.loadDetails();
            });
        }
    }

    /**
     * Klick auf "Abbrechen"
     */
    clickCancel(): void {
        // EditMode verlassen
        this.editMode = false;
        // Daten zurücksetzen
        this.resetData();
        // Daten Laden
        this.loadDetails();
    }

    /**
     * Daten zurücksetzen (z.B. wenn Einrichtung gewechselt wird)
     */
    resetData(): void {
        this.publicNote = '';
        this.noteId = null;
        this.readOnlyNote = null;
    }

}
